import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import Header from '../../components/Header';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent } from '../../components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { QuickAddButton } from '../../components/QuickAddProduct';
import { MarketplaceAd, useAdTimer, useAdFreeStatus, AdFreeSubscriptionBanner } from '../../components/MarketplaceAd';
import { MarketplaceStatsBar, TrustBadges, SoldBadge, StripeSecurityBadge } from '../../components/TrustSignals';
import { FomoNotifications } from '../../components/FomoNotifications';
import axios from 'axios';
import { 
  Search, Plus, Package, Filter, Grid, List, MapPin, 
  Tag, Loader2, MessageCircle, Heart, Star, Zap, Crown, CheckCircle, ShoppingBag
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const categories = [
  { value: 'all', label: 'All Categories' },
  { value: 'auto_parts', label: 'Auto Parts' },
  { value: 'tools', label: 'Tools & Equipment' },
  { value: 'vehicles', label: 'Vehicles' },
  { value: 'accessories', label: 'Accessories' },
  { value: 'other', label: 'Other' }
];

const conditions = {
  new: 'New',
  used: 'Used',
  refurbished: 'Refurbished'
};

export default function Marketplace() {
  const { user, getAuthHeaders } = useAuth();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('all');
  const [viewMode, setViewMode] = useState('grid');
  const [showSubscriptionBanner, setShowSubscriptionBanner] = useState(false);
  
  // Ad-free subscription status
  const { hasSubscription, refresh: refreshSubscription } = useAdFreeStatus();
  
  // Ad timer - show ad every 25 minutes of scrolling (only for customers without subscription)
  const isCustomer = user?.user_type === 'customer';
  const { showAd, dismissAd } = useAdTimer(hasSubscription || !isCustomer);

  useEffect(() => {
    fetchProducts();
  }, [category]);
  
  // Show subscription banner occasionally for customers without subscription
  useEffect(() => {
    if (isCustomer && !hasSubscription) {
      // Show banner after 10 seconds, 20% chance
      const timer = setTimeout(() => {
        if (Math.random() < 0.2) {
          setShowSubscriptionBanner(true);
        }
      }, 10000);
      return () => clearTimeout(timer);
    }
  }, [isCustomer, hasSubscription]);

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (category && category !== 'all') params.append('category', category);
      if (search) params.append('search', search);
      
      const response = await axios.get(`${API}/marketplace/products?${params.toString()}`);
      setProducts(response.data);
    } catch (error) {
      console.error('Failed to fetch products:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchProducts();
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="marketplace-page">
      <Header />
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 font-['Chivo']">Marketplace</h1>
            <p className="text-gray-600 mt-1 text-sm sm:text-base">Buy and sell auto parts, tools, and more</p>
          </div>
          
          {user && (
            <div className="flex flex-wrap gap-2">
              <QuickAddButton onProductAdded={() => fetchProducts()} />
              <Link to="/marketplace/sell" data-testid="sell-button">
                <Button className="bg-blue-600 hover:bg-blue-700">
                  <Plus className="w-4 h-4 mr-2" />
                  Sell Something
                </Button>
              </Link>
            </div>
          )}
        </div>

        {/* Trust Stats Bar */}
        <MarketplaceStatsBar />
        
        {/* Ad-Free Subscription Banner (show occasionally for customers without subscription) */}
        {isCustomer && !hasSubscription && showSubscriptionBanner && (
          <div className="mb-6">
            <AdFreeSubscriptionBanner 
              onSubscribe={() => { 
                refreshSubscription(); 
                setShowSubscriptionBanner(false); 
              }} 
            />
          </div>
        )}
        
        {/* Sponsored Ad (shown every 25 min of scrolling for non-subscribers) */}
        {showAd && isCustomer && !hasSubscription && (
          <div className="mb-6">
            <MarketplaceAd 
              category={category !== 'all' ? category : undefined} 
              onClose={dismissAd}
            />
          </div>
        )}

        {/* Filters */}
        <div className="bg-white p-4 rounded-lg border border-gray-200 mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            <form onSubmit={handleSearch} className="flex-1 flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search products..."
                  className="pl-10 h-11"
                  data-testid="search-input"
                />
              </div>
              <Button type="submit" variant="outline" data-testid="search-button">
                Search
              </Button>
            </form>
            
            <div className="flex gap-2">
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger className="w-[180px]" data-testid="category-select">
                  <Filter className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="Category" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((cat) => (
                    <SelectItem key={cat.value} value={cat.value}>
                      {cat.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <div className="flex border border-gray-200 rounded-md">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-2 ${viewMode === 'grid' ? 'bg-gray-100' : ''}`}
                  data-testid="grid-view"
                >
                  <Grid className="w-5 h-5 text-gray-600" />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-2 ${viewMode === 'list' ? 'bg-gray-100' : ''}`}
                  data-testid="list-view"
                >
                  <List className="w-5 h-5 text-gray-600" />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Products */}
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-10 h-10 text-blue-600 animate-spin" />
          </div>
        ) : products.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Package className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-2">No Products Found</h3>
              <p className="text-gray-600 mb-6">
                {search || category !== 'all' 
                  ? 'Try adjusting your filters'
                  : 'Be the first to list something for sale!'}
              </p>
              {user && (
                <Link to="/marketplace/sell">
                  <Button className="bg-blue-600 hover:bg-blue-700">
                    <Plus className="w-4 h-4 mr-2" />
                    List Your First Item
                  </Button>
                </Link>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className={viewMode === 'grid' 
            ? 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6' 
            : 'space-y-4'
          }>
            {products.map((product) => (
              <Link 
                key={product.product_id} 
                to={`/marketplace/product/${product.product_id}`}
                data-testid={`product-${product.product_id}`}
              >
                <Card className={`overflow-hidden hover:shadow-lg transition-shadow ${
                  viewMode === 'list' ? 'flex' : ''
                } ${product.status === 'sold' ? 'opacity-75' : ''}`}>
                  {/* Product Image */}
                  <div className={`bg-gray-100 relative ${
                    viewMode === 'grid' ? 'h-48' : 'w-48 h-32 flex-shrink-0'
                  } flex items-center justify-center`}>
                    {product.images && product.images.length > 0 ? (
                      <img 
                        src={product.images[0]} 
                        alt={product.title}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <Package className="w-12 h-12 text-gray-400" />
                    )}
                    {/* Sold Badge */}
                    {product.status === 'sold' && (
                      <div className="absolute top-2 left-2 bg-red-600 text-white px-2 py-1 rounded-md text-xs font-bold flex items-center gap-1 shadow-lg">
                        <CheckCircle className="w-3 h-3" />
                        SOLD
                      </div>
                    )}
                  </div>
                  
                  <CardContent className={`p-4 ${viewMode === 'list' ? 'flex-1' : ''}`}>
                    <div className="flex items-start justify-between mb-2">
                      <h3 className="font-semibold text-gray-900 line-clamp-1">{product.title}</h3>
                      <span className={`text-xs px-2 py-1 rounded-full ${
                        product.condition === 'new' ? 'bg-green-100 text-green-700' :
                        product.condition === 'refurbished' ? 'bg-blue-100 text-blue-700' :
                        'bg-gray-100 text-gray-700'
                      }`}>
                        {conditions[product.condition]}
                      </span>
                    </div>
                    
                    <p className="text-sm text-gray-600 line-clamp-2 mb-3">
                      {product.description}
                    </p>
                    
                    <div className="flex items-center justify-between">
                      <span className="text-xl font-bold text-blue-600">
                        ${product.price.toFixed(2)}
                      </span>
                      <div className="flex items-center gap-2 text-sm text-gray-500">
                        <MapPin className="w-4 h-4" />
                        <span className="truncate max-w-[100px]">{product.location}</span>
                      </div>
                    </div>
                    
                    <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-100">
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-gray-500">
                          by {product.seller_name}
                        </span>
                        {product.seller_rating && (
                          <div className="flex items-center gap-1">
                            <Star className="w-3 h-3 text-yellow-400 fill-yellow-400" />
                            <span className="text-xs text-gray-500">{product.seller_rating}</span>
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-1 text-xs text-gray-400">
                        <MapPin className="w-3 h-3" />
                        <span className="truncate max-w-[80px]">{product.seller_location || product.location}</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        )}

        {/* Trust Badges Section */}
        <div className="mt-12 py-8 border-t border-gray-200">
          <div className="text-center mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Shop with Confidence</h3>
            <p className="text-sm text-gray-600">Every transaction is protected</p>
          </div>
          <TrustBadges size="md" />
          <div className="mt-6 flex justify-center">
            <StripeSecurityBadge variant="large" />
          </div>
        </div>
      </main>

      {/* FOMO Notifications for Marketplace */}
      <FomoNotifications 
        enabled={!user}
        interval={50000}
        initialDelay={12000}
      />
    </div>
  );
}
